import { useMemo } from "react";

import { DataPointValue, Settings, State } from "../types";
import { addCorrections, AddCorrectionsAction } from "./reducers/addCorrections";
import { addGroup, AddGroupAction } from "./reducers/addGroup";
import { addValue, AddValueAction } from "./reducers/addValue";
import { clearActiveDataPoint, ClearActiveDataPointAction } from "./reducers/clearActiveDataPoint";
import { clearAllValues, ClearAllValuesAction } from "./reducers/clearAllValues";
import { clearGroup, ClearGroupAction } from "./reducers/clearGroup";
import { clearValue, ClearValueAction } from "./reducers/clearValue";
import { markDataAsSaved, MarkDataAsSavedAction } from "./reducers/markDataAsSaved";
import { moveToNextDataPoint, MoveToNextDataPointAction } from "./reducers/nextDataPoint";
import { moveToPrevDataPoint, MoveToPrevDataPointAction } from "./reducers/prevDataPoint";
import { removeAnswer, RemoveAnswerAction } from "./reducers/removeAnswer";
import { removeGroup, RemoveGroupAction } from "./reducers/removeGroup";
import {
  selectActiveDataPoint,
  SelectActiveDataPointAction,
} from "./reducers/selectActiveDataPoint";
import { selectBlock, SelectBlockAction } from "./reducers/selectBlock";
import {
  setConfidenceThreshold,
  SetConfidenceThresholdAction,
} from "./reducers/setConfidenceThreshold";
import { setScrollToBlock, SetScrollToBlockAction } from "./reducers/setScrollToBlock";
import { updateAnswerValue, UpdateAnswerValueAction } from "./reducers/updateAnswerValue";
import { updatePointPrompt, UpdatePointPromptAction } from "./reducers/updatePointPrompt";

type Action =
  | AddCorrectionsAction
  | AddValueAction<any>
  | AddGroupAction
  | ClearActiveDataPointAction
  | ClearAllValuesAction
  | ClearGroupAction
  | ClearValueAction
  | MarkDataAsSavedAction
  | MoveToNextDataPointAction
  | MoveToPrevDataPointAction
  | RemoveAnswerAction
  | RemoveGroupAction
  | SelectActiveDataPointAction
  | SelectBlockAction
  | SetConfidenceThresholdAction
  | SetScrollToBlockAction
  | UpdateAnswerValueAction
  | UpdatePointPromptAction

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD CORRECTIONS":
      return addCorrections(state, action);
    case "ADD GROUP":
      return addGroup(state, action);
    case "ADD VALUE":
      return addValue(state, action);
    case "CLEAR ACTIVE DATA POINT":
      return clearActiveDataPoint(state, action);
    case "CLEAR ALL VALUES":
      return clearAllValues(state, action);
    case "CLEAR GROUP":
      return clearGroup(state, action);
    case "CLEAR VALUE":
      return clearValue(state, action);
    case "MARK DATA AS SAVED":
      return markDataAsSaved(state, action);
    case "MOVE TO NEXT DATA POINT":
      return moveToNextDataPoint(state, action);
    case "MOVE TO PREV DATA POINT":
      return moveToPrevDataPoint(state, action);
    case "SELECT ACTIVE DATA POINT":
      return selectActiveDataPoint(state, action);
    case "SELECT BLOCK":
      return selectBlock(state, action);
    case "SET CONFIDENCE THRESHOLD":
      return setConfidenceThreshold(state, action);
    case "SET SCROLL TO BLOCK":
      return setScrollToBlock(state, action);
    case "REMOVE ANSWER":
      return removeAnswer(state, action);
    case "REMOVE GROUP":
      return removeGroup(state, action);
    case "UPDATE ANSWER VALUE":
      return updateAnswerValue(state, action);
    case "UPDATE POINT PROMPT":
      return updatePointPrompt(state, action);
  }
};

type Dispatch = (action: Action) => void;
export const useStateCallbacks = (review: boolean, dispatch: Dispatch, settings: Settings) =>
  useMemo(
    () => ({
      onAddCorrections: (blockId: string, correction: string) => {
        if (review) {
          return;
        }
        dispatch({
          blockId,
          correction,
          type: "ADD CORRECTIONS",
        });
      },
      onAddGroup: (taxonomyKeys: Array<string>, valueKeys: Array<string>) => {
        if (review) {
          return;
        }
        dispatch({
          copyValues: null,
          keys: valueKeys,
          taxonomyKeys,
          type: "ADD GROUP",
        });
      },
      onAddValue: (dataPointKey: string, key: string) => {
        if (review) {
          return;
        }
        dispatch({
          copyValue: null,
          dataPointKey,
          key,
          type: "ADD VALUE",
        });
      },
      onClearActiveDataPoint: () => {
        dispatch({
          type: "CLEAR ACTIVE DATA POINT",
        });
      },
      onClearAllDataPoints: () => {
        dispatch({
          type: "CLEAR ALL VALUES",
        });
      },
      onClearGroup: (key: string) => {
        dispatch({
          key,
          type: "CLEAR GROUP",
        });
      },
      onClearValue: (valueKey: string) => {
        if (review) {
          return;
        }
        dispatch({
          type: "CLEAR VALUE",
          valueKey,
        });
      },
      onDuplicateAnswer: <T>(dataPointKey: string, key: string, copyValue: DataPointValue<T>) => {
        if (review) {
          return;
        }
        dispatch({
          copyValue,
          dataPointKey,
          key,
          type: "ADD VALUE",
        });
      },
      onDuplicateGroup: (
        copyValues: Array<DataPointValue<any>>,
        taxonomyKeys: Array<string>,
        valueKeys: Array<string>,
      ) => {
        if (review) {
          return;
        }
        dispatch({
          copyValues,
          keys: valueKeys,
          taxonomyKeys,
          type: "ADD GROUP",
        });
      },
      onMarkDataAsSaved: () => {
        if (review) {
          return;
        }
        dispatch({ type: "MARK DATA AS SAVED" });
      },
      onMoveToNextDataPoint: () => {
        dispatch({ type: "MOVE TO NEXT DATA POINT" });
      },
      onMoveToPrevDataPoint: () => {
        dispatch({ type: "MOVE TO PREV DATA POINT" });
      },
      onRemoveAnswer: (key: string) => {
        if (review) {
          return;
        }
        dispatch({
          key,
          type: "REMOVE ANSWER",
        });
      },
      onRemoveGroup: (keys: Array<string>) => {
        if (review) {
          return;
        }
        dispatch({
          keys,
          type: "REMOVE GROUP",
        });
      },
      onSelectBlock: (idRef: string, blockText: string, replaceValue: boolean) => {
        if (review) {
          return;
        }
        dispatch({
          blockText,
          idRef,
          replaceValue,
          type: "SELECT BLOCK",
        });
      },
      onSelectDataPoint: (key: string) => {
        dispatch({
          key,
          type: "SELECT ACTIVE DATA POINT",
        });
        if (settings.automaticallyScrollDocument) {
          dispatch({
            blockId: key,
            type: "SET SCROLL TO BLOCK",
          });
        }
      },
      onSetScrollToBlock: (blockId: string) => {
        dispatch({
          blockId,
          type: "SET SCROLL TO BLOCK",
        });
      },
      onUpdateConfidenceThreshold: (confidenceThreshold: number) => {
        dispatch({
          confidenceThreshold,
          type: "SET CONFIDENCE THRESHOLD",
        });
      },
      onUpdateValue: (key: string, value: any, valueRaw: string) => {
        if (review) {
          return;
        }
        dispatch({
          key,
          type: "UPDATE ANSWER VALUE",
          value,
          valueRaw,
        });
      },
      updatePointPrompt: (key: string, prompt: any) => {
        if (review) {
          return;
        }
        dispatch({
          key,
          type: "UPDATE POINT PROMPT",
          prompt,
        });
      }
    }),
    [dispatch, review, settings],
  );

export type StateCallbacks = ReturnType<typeof useStateCallbacks>;
